import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Loader, AlertCircle, Globe, Calendar } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell
} from 'recharts';
import { analyzeChannel } from '../services/api';
import DataBadge from '../components/DataBadge';
import axios from 'axios';

const fadeUp = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

const API_BASE = 'https://youtube-analytics-backend-production.up.railway.app';

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('uz-UZ', { year: 'numeric', month: 'long', day: 'numeric' });
}

const COLORS = ['#50C878', '#6fd99a', '#06b6d4', '#50C878', '#f59e0b'];

function AvatarFallback({ name, size = 80 }) {
  const letter = name?.charAt(0)?.toUpperCase() || '?';
  const colors = ['#50C878', '#6fd99a', '#06b6d4', '#50C878', '#f59e0b'];
  const color = colors[(name?.length || 0) % colors.length] || colors[0];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, ${color}, ${color}99)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700, color: '#fff', flexShrink: 0,
    }}>{letter}</div>
  );
}

function ChannelAvatar({ src, name, size = 88 }) {
  const [err, setErr] = React.useState(false);
  if (!err && src) {
    return (
      <img
        src={src} alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(16,185,129,0.4)', flexShrink: 0 }}
        onError={() => setErr(true)}
      />
    );
  }
  return <AvatarFallback name={name} size={size} />;
}

// Delay helper for staggered CSS animations
function delay(ms) { return { animationDelay: `${ms}ms` }; }

export default function ChannelAnalysis() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  async function fetchAiAnalysis(channelData) {
    setAiLoading(true);
    setAiText('');
    try {
      const statsText = `
Kanal nomi: ${channelData.title}
Obunachilar: ${fmt(channelData.subscriberCount)}
Jami ko'rishlar: ${fmt(channelData.viewCount)}
Videolar soni: ${channelData.videoCount}
Video boshiga o'rtacha ko'rish: ${fmt(channelData.avgViewsPerVideo)}
So'nggi videolar o'rtacha ko'rishi: ${fmt(channelData.avgRecentViews)}
Engagement rate: ${channelData.engagementRate}%
Yuklash chastotasi: har ${channelData.avgDaysBetweenUploads || '?'} kunda
Mamlakat: ${channelData.country}
      `.trim();

      const res = await axios.post(`${API_BASE}/api/chat`, {
        categoryName: 'YouTube kanal tahlili',
        messages: [{
          role: 'user',
          content: `Bu YouTube kanalini chuqur tahlil qiling:\n\n${statsText}\n\nKamida 200 so'z yozing. Umumiy gaplar yozmang. Aniq raqamlar va sabablar bilan tushuntiring. Kanalning 3 ta kuchli tomoni va 3 ta yaxshilash kerak bo'lgan tomonini batafsil ayting. Har bir nuqtani asoslab yozing. O'zbek tilida, jadval emas, oddiy matn shaklida yozing. Kuchli tomonlarni "💪 KUCHLI TOMONLAR:" deb boshlang, zaif tomonlarni "⚠️ YAXSHILASH KERAK:" deb boshlang. Oxirida qisqa strategik maslahat bering.`
        }]
      });
      setAiText(res.data.reply || '');
    } catch {
      setAiText("AI tahlilni yuklab bo'lmadi.");
    } finally {
      setAiLoading(false);
    }
  }

  async function handleAnalyze() {
    if (!input.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    setAiText('');
    setShowResults(false);
    try {
      const res = await analyzeChannel(input.trim());
      setData(res.data);
      // Small delay so DOM mounts before animation starts
      setTimeout(() => setShowResults(true), 30);
      fetchAiAnalysis(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Kanal topilmadi. Linkni tekshiring.");
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) { if (e.key === 'Enter') handleAnalyze(); }

  const chartData = data?.recentVideos?.map((v, i) => ({
    name: v.title.length > 15 ? v.title.slice(0, 15) + '…' : v.title,
    views: v.views,
    color: COLORS[i % COLORS.length]
  })) || [];

  function renderAiBox() {
    if (aiLoading) {
      return (
        <div className="result-block ai-box-scale lift" style={{ ...cardStyle, ...aiBoxStyle, ...delay(300) }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8' }}>
            <Loader size={18} className="spin" />
            AI tahlil qilmoqda...
            <div className="dot-pulse" style={{ color: '#50C878' }}><span /><span /><span /></div>
          </div>
          {/* Shimmer placeholders */}
          {[80, 60, 90, 50].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: 14, width: `${w}%`, marginTop: 10, borderRadius: 6 }} />
          ))}
        </div>
      );
    }
    if (!aiText) return null;

    const parts = aiText.split(/(💪 KUCHLI TOMONLAR:|⚠️ YAXSHILASH KERAK:)/g);

    return (
      <div className="result-block ai-box-scale lift anim-pulse" style={{ ...cardStyle, ...aiBoxStyle, ...delay(300) }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: '#50C878' }}>🤖 AI Tahlili</h3>
        <div style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
          {parts.map((part, i) => {
            if (part === '💪 KUCHLI TOMONLAR:')
              return <div key={i} style={{ fontWeight: 700, color: '#50C878', marginTop: i > 0 ? 16 : 0, fontSize: 15 }}>💪 Kuchli tomonlar</div>;
            if (part === '⚠️ YAXSHILASH KERAK:')
              return <div key={i} style={{ fontWeight: 700, color: '#f59e0b', marginTop: 16, fontSize: 15 }}>⚠️ Yaxshilash kerak</div>;
            return <span key={i}>{part}</span>;
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingTop: 88, paddingBottom: 72 }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 20px' }}>

        {/* Page Header */}
        <div className="anim-fadeInUp" style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontSize: 30, fontWeight: 800, marginBottom: 8 }}>
            <span style={{ background: 'linear-gradient(135deg,#50C878,#004B3B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Kanal Tahlili
            </span>
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 15 }}>
            Istalgan YouTube kanalini URL yoki @username orqali tahlil qiling
          </p>
        </div>

        {/* Search Row */}
        <div className="anim-fadeInUp" style={{ display: 'flex', gap: 12, marginBottom: 32, animationDelay: '80ms' }}>
          <div className="analyze-input-wrap">
            <Search size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b', pointerEvents: 'none' }} />
            <input
              ref={inputRef}
              className="analyze-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://youtube.com/@MrBeast yoki @MrBeast"
            />
          </div>
          <button
            className="analyze-btn"
            onClick={handleAnalyze}
            disabled={loading || !input.trim()}
          >
            {loading ? <Loader size={16} className="spin" /> : <Search size={16} />}
            {loading ? 'Tahlil...' : 'Tahlil qilish'}
          </button>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="anim-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[240, 300, 200].map((h, i) => (
              <div key={i} className="skeleton" style={{ height: h, borderRadius: 16 }} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="anim-scaleIn" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderRadius: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', marginBottom: 24 }}>
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        {/* Results — staggered fade-in */}
        {data && !loading && showResults && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* 1. Channel header card */}
            <motion.div
              className="lift"
              style={cardStyle}
              variants={fadeUp} initial="initial" animate="animate"
              transition={{ duration: 0.4, delay: 0, ease: 'easeOut' }}
              whileHover={{ y: -4, scale: 1.01, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
            >
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div
                  style={{ cursor: 'pointer' }}
                  title="YouTube kanalini ochish"
                  onClick={() => {
                    const channelId = data.channelId || data.id;
                    const handle = data.customUrl;
                    console.log('[ChannelAnalysis] Opening channel:', { channelId, handle });
                    const url = handle
                      ? `https://www.youtube.com/${handle}`
                      : channelId
                        ? `https://www.youtube.com/channel/${channelId}`
                        : `https://www.youtube.com`;
                    window.open(url, '_blank');
                  }}
                >
                  <ChannelAvatar src={data.thumbnail} name={data.title} size={88} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6 }}>{data.title}</h2>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8, fontSize: 13, color: '#94a3b8' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Globe size={13} />{data.country}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={13} />Tashkil: {fmtDate(data.publishedAt)}</span>
                  </div>
                  {data.description && (
                    <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                      {data.description}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* 2. Stats table */}
            <motion.div
              className="lift"
              style={cardStyle}
              variants={fadeUp} initial="initial" animate="animate"
              transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
            >
              <h3 style={sectionTitle}>📊 Statistika</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Obunachilar',                        fmt(data.subscriberCount),                                              true,  null],
                    ["Jami ko'rishlar",                    fmt(data.viewCount),                                                    true,  null],
                    ['Videolar soni',                      fmt(data.videoCount),                                                   true,  null],
                    ["Video boshiga o'rtacha ko'rish",     fmt(data.avgViewsPerVideo),                                             true,  null],
                    ["So'nggi videolar o'rtacha ko'rishi", fmt(data.avgRecentViews),                                               true,  null],
                    ['Engagement rate',                    data.engagementRate + '%',                                              true,  null],
                    ['Yuklash chastotasi',                 data.avgDaysBetweenUploads ? `har ${data.avgDaysBetweenUploads} kunda` : '—', true, null],
                    ['Taxminiy oylik daromad',             data.estimatedMonthlyRevenue ? `$${data.estimatedMonthlyRevenue.toLocaleString()}` : '—', false, 'Taxminiy daromad'],
                  ].map(([label, value, isReal, badgeLabel], i) => (
                    <tr
                      key={label}
                      className="result-row-slide"
                      style={{
                        borderBottom: '1px solid rgba(148,163,184,0.1)',
                        background: i % 2 === 0 ? 'rgba(16,185,129,0.03)' : 'transparent',
                        ...delay(120 + i * 55),
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontSize: 14, color: '#94a3b8', width: '55%' }}>{label}</td>
                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: '#50C878' }}>
                        {value}
                        <DataBadge isReal={isReal} label={badgeLabel} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            {/* 3. AI Analysis */}
            {(aiLoading || aiText) && renderAiBox()}

            {/* 4. Bar chart */}
            {chartData.length > 0 && (
              <motion.div
                className="lift card-glow"
                style={cardStyle}
                variants={fadeUp} initial="initial" whileInView="animate"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }}
              >
                <h3 style={sectionTitle}>📈 So'nggi videolar ko'rishlari</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <defs>
                      {COLORS.map((c, i) => (
                        <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={c} stopOpacity={1} />
                          <stop offset="100%" stopColor={c} stopOpacity={0.5} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3d4' }} />
                    <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: '#9ca3d4' }} />
                    <RTooltip
                      formatter={v => [fmt(v), "Ko'rishlar"]}
                      contentStyle={{ background: 'rgba(10,14,26,0.92)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#f1f5f9', fontSize: 13 }}
                    />
                    <Bar dataKey="views" radius={[8, 8, 0, 0]} isAnimationActive animationDuration={1500} animationEasing="ease-out" animationBegin={200}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={`url(#barGrad${i % COLORS.length})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>
            )}

            {/* 5. Recent videos table */}
            {data.recentVideos?.length > 0 && (
              <motion.div
                className="lift"
                style={cardStyle}
                variants={fadeUp} initial="initial" whileInView="animate"
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              >
                <h3 style={sectionTitle}>🎬 So'nggi videolar</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
                        {["Video nomi", "Ko'rishlar", 'Like', 'Izoh', 'Sana'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentVideos.map((v, i) => (
                        <tr
                          key={i}
                          className="result-row-slide"
                          style={{ borderBottom: '1px solid rgba(148,163,184,0.08)', ...delay(520 + i * 70) }}
                        >
                          <td style={{ padding: '11px 12px', fontSize: 13, maxWidth: 240 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.title}>{v.title}</div>
                          </td>
                          <td style={{ padding: '11px 12px', fontSize: 13, fontWeight: 600, color: '#50C878' }}>{fmt(v.views)}</td>
                          <td style={{ padding: '11px 12px', fontSize: 13, color: '#50C878' }}>{fmt(v.likes)}</td>
                          <td style={{ padding: '11px 12px', fontSize: 13, color: '#94a3b8' }}>{fmt(v.comments)}</td>
                          <td style={{ padding: '11px 12px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(v.publishedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

const cardStyle = {
  background: 'var(--surface, rgba(255,255,255,0.58))',
  borderRadius: 16,
  padding: '24px',
  border: '1px solid var(--border, rgba(148,163,184,0.15))',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
};

const aiBoxStyle = {
  border: '1px solid rgba(99,102,241,0.25)',
  background: 'rgba(99,102,241,0.06)',
};

const sectionTitle = {
  fontSize: 15,
  fontWeight: 700,
  marginBottom: 16,
  color: 'inherit',
};
